import React from 'react';
import clsx from 'clsx';

import {assertUnreachable} from 'common/util/typescript';
import {formatTextReact} from 'common/util/format-text-react';
import {SvgIconReact} from 'common/components/svg-icon';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {MarkdownReact} from 'common/components/markdown';
import {withStyles} from 'common/util/react';
import {LocaleSafeString} from 'common/i18n';

import {WarningTheme} from './constants';
import css from './index.module.styl?inline';
import styles from './index.module.styl';

export interface WarningProps {
  theme: WarningTheme;
  headerText: LocaleSafeString;
  bodyText?: LocaleSafeString;
  ctaHref?: string;
  ctaText?: LocaleSafeString;
  imgSrc?: string;
  small?: boolean;
  onCtaClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

function Icon({theme, imgSrc, small}: {theme: WarningTheme; imgSrc?: string; small: boolean}) {
  if (imgSrc) {
    return <img src={imgSrc} alt='' />;
  }
  const sizeClass = small ? styles.iconSmall : styles.icon;
  switch (theme) {
    case WarningTheme.Error:
      return <SvgIconReact icon='warning' className={clsx(sizeClass, styles.iconWarning)} />;
    case WarningTheme.Sensitive:
      return <SvgIconReact icon='lock' className={clsx(sizeClass, styles.iconLock)} />;
    case WarningTheme.NoData:
      return <div className={styles.mpWarningNoDataSvg} />;
    case WarningTheme.NoIcon:
      return null;
    case WarningTheme.Default:
      return <div className={styles.mpWarningLaptopSvg} />;
    case WarningTheme.Loading:
      return <SpinnerReact size={SpinnerSize.Large} />;
    case WarningTheme.Tip:
      return null;
    default:
      assertUnreachable(theme);
  }
}

function BodyText({text}: {text: string}) {
  if (!text) {
    return null;
  }

  return (
    <div className={styles.bodyText}>
      {text.split(`\n`).map((line, idx) => (
        <React.Fragment key={idx}>
          {idx ? <br /> : null}
          {formatTextReact(line)}
        </React.Fragment>
      ))}
    </div>
  );
}

export function Warning({
  theme,
  headerText,
  bodyText,
  ctaHref,
  ctaText,
  imgSrc,
  small,
  onCtaClick,
  className,
  style,
  children,
}: WarningProps) {
  const isSmall = !!small;
  const isTip = theme === WarningTheme.Tip;

  const handleCtaClick = (ev: React.MouseEvent) => {
    if (!ctaHref) {
      ev.preventDefault();
    }
    onCtaClick?.();
  };

  return (
    <div className={clsx(styles.mpWarningContainer, {[styles.small]: isSmall}, className)} style={style}>
      <div className={clsx(styles.mpWarningContent, {[styles.small]: isSmall})}>
        <Icon theme={theme} imgSrc={imgSrc} small={isSmall} />
        {headerText && <h1 className={clsx(styles.header, {[styles.themeTip]: isTip})}>{headerText}</h1>}
        {isTip ? (
          <MarkdownReact text={bodyText ?? ``} className={styles.markdownTip} />
        ) : (
          <BodyText text={bodyText ?? ``} />
        )}
      </div>
      {children}
      {ctaText && (
        <a className={styles.mpWarningCta} href={ctaHref} onClick={onCtaClick ? handleCtaClick : undefined}>
          {ctaText}
          <SvgIconReact icon='arrow-right' className={styles.ctaIcon} />
        </a>
      )}
    </div>
  );
}

export const WarningReact = withStyles(Warning, css);
